import { en } from './en';
import type { MessageKey } from './message-key';

export type Lang = 'en' | 'bn';

// Default language is English (CLAUDE.md).
export const DEFAULT_LANG: Lang = 'en';

// English is always bundled (the default and the fallback). Bangla is a separate
// chunk that a surface loads on demand (`loadLang`) — most visitors never pay for a
// dictionary they will not read (INF-50 entry budgets). Code that must render
// Bangla synchronously (the notify consumer, test setups) registers it up front.
const dictionaries: Partial<Record<Lang, Partial<Record<MessageKey, string>>>> = { en };
// Namespaces a lazy chunk brings with it (the website's Listings strings): looked up
// after the language's own dictionary.
const extras: Partial<Record<Lang, Partial<Record<MessageKey, string>>>> = {};

// Typed lookup. `key` is constrained to MessageKey, so only real keys compile.
// Falls back to English while a dictionary is not loaded or a key is absent.
export function t(lang: Lang, key: MessageKey): string {
  return dictionaries[lang]?.[key] ?? extras[lang]?.[key] ?? dictionaries.en?.[key] ?? extras.en?.[key] ?? key;
}

/** Add a namespace of messages for `lang` (a lazy chunk's own strings). */
export function registerMessages(lang: Lang, messages: Partial<Record<MessageKey, string>>): void {
  extras[lang] = { ...extras[lang], ...messages };
}

export function isLangLoaded(lang: Lang): boolean {
  return dictionaries[lang] !== undefined;
}

export function registerDictionary(lang: Lang, dictionary: Record<MessageKey, string>): void {
  dictionaries[lang] = dictionary;
}

let bnLoading: Promise<void> | null = null;

// Resolves once `lang` is ready for `t`. English is immediate; Bangla is fetched
// once and shared by every caller.
export function loadLang(lang: Lang): Promise<void> {
  if (isLangLoaded(lang)) return Promise.resolve();
  // A failed load is forgotten, so the next call tries again instead of failing forever.
  bnLoading ??= import('./bn').then(
    (m) => registerDictionary('bn', m.bn),
    (err: unknown) => {
      bnLoading = null;
      throw err;
    },
  );
  return bnLoading;
}
